import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Users, Wallet, CalendarCheck, Search, TrendingUp, Palette, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import api from "../lib/api";
import { formatINR } from "../mock";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";

const statusStyle = {
  "Pending Payment": "bg-orange-50 text-orange-700",
  "Paid": "bg-emerald-50 text-emerald-700",
  "Confirmed": "bg-blue-50 text-blue-700",
  "Cancelled": "bg-red-50 text-red-600",
};

export default function Admin() {
  const { isAdmin, ready } = useAuth();
  const { theme, setTheme, themes } = useTheme();
  const [bookings, setBookings] = useState([]);
  const [q, setQ] = useState("");

  const load = () => api.get("/admin/bookings").then((res) => setBookings(res.data)).catch(() => {});
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      load();
    } catch (e) {}
  };

  const stats = useMemo(() => {
    const revenue = bookings.reduce((s, b) => s + (b.total || 0), 0);
    const travelers = bookings.reduce((s, b) => s + (b.travelers || 0), 0);
    return { total: bookings.length, revenue, travelers };
  }, [bookings]);

  const filtered = bookings.filter((b) =>
    [b.name, b.email, b.tourTitle, b.id].join(" ").toLowerCase().includes(q.toLowerCase())
  );

  if (ready && !isAdmin) return <Navigate to="/login" replace />;

  const cards = [
    { icon: CalendarCheck, label: "Total Bookings", value: stats.total, color: "bg-coral-600" },
    { icon: Wallet, label: "Pipeline Revenue", value: formatINR(stats.revenue), color: "bg-emerald-600" },
    { icon: Users, label: "Total Travelers", value: stats.travelers, color: "bg-violet-600" },
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      <section className="hero-dark pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-5">
          <p className="text-xs font-semibold tracking-widest text-amber-400 uppercase">Super Admin</p>
          <h1 className="mt-2 text-4xl font-bold text-white">Bookings Dashboard</h1>
          <p className="mt-2 text-slate-300">Manage all customer bookings across Flycher Travels.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 -mt-12">
        <div className="grid gap-5 sm:grid-cols-3">
          {cards.map((c) => (
            <div key={c.label} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex items-center gap-4">
              <span className={`grid place-items-center w-12 h-12 rounded-xl text-white ${c.color}`}><c.icon className="w-6 h-6" /></span>
              <div>
                <p className="text-sm text-slate-500">{c.label}</p>
                <p className="font-display text-2xl font-bold text-slate-900">{c.value}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* THEME SWITCHER */}
      <section className="max-w-7xl mx-auto px-5 mt-6">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-1">
            <Palette className="w-5 h-5 text-coral-600" />
            <h2 className="font-display font-bold text-slate-900">Site Theme</h2>
          </div>
          <p className="text-sm text-slate-500 mb-5">Pick the accent palette for the whole website. Changes apply instantly.</p>
          <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
            {themes.map((t) => {
              const selected = theme === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTheme(t.key)}
                  className={`group text-left rounded-xl border-2 p-4 transition-all ${
                    selected ? "border-coral-600 ring-2 ring-coral-600/20" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {t.swatch.map((c) => (
                        <span key={c} className="w-7 h-7 rounded-full border border-black/5" style={{ background: c }} />
                      ))}
                    </div>
                    {selected ? (
                      <span className="flex items-center gap-1 text-xs font-semibold text-coral-600">
                        <Check className="w-4 h-4" /> Active
                      </span>
                    ) : (
                      <span className="text-xs font-medium text-slate-400 group-hover:text-slate-600">Apply</span>
                    )}
                  </div>
                  <p className="mt-3 font-semibold text-slate-900">{t.name}</p>
                  <p className="text-sm text-slate-500">{t.tagline}</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-10">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between gap-3">
            <h2 className="font-display font-bold text-slate-900 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-coral-600" /> All Bookings</h2>
            <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 w-64">
              <Search className="w-4 h-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search bookings..." className="border-0 shadow-none focus-visible:ring-0 px-0 bg-transparent" />
            </div>
          </div>
          {filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-500">No bookings yet. They’ll appear here as customers book.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Booking</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Tour</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Pax</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell className="font-mono text-xs text-slate-500">#{b.id}</TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900">{b.name}</div>
                        <div className="text-xs text-slate-500">{b.email}</div>
                        <div className="text-xs text-slate-500">{b.phone}</div>
                      </TableCell>
                      <TableCell className="max-w-[220px]">
                        <div className="text-sm text-slate-800 line-clamp-2">{b.tourTitle}</div>
                        <div className="text-xs text-slate-500">{b.tourLocation}</div>
                      </TableCell>
                      <TableCell className="text-sm">{b.date}</TableCell>
                      <TableCell className="text-sm">{b.travelers}</TableCell>
                      <TableCell className="font-display font-semibold">{formatINR(b.total)}</TableCell>
                      <TableCell>
                        <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                          <SelectTrigger className={`h-8 w-[150px] text-[11px] font-bold border-0 ${statusStyle[b.status] || "bg-slate-100 text-slate-600"}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Pending Payment">Pending Payment</SelectItem>
                            <SelectItem value="Paid">Paid</SelectItem>
                            <SelectItem value="Confirmed">Confirmed</SelectItem>
                            <SelectItem value="Cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
